import React, { useState } from 'react';
import { 
  Search, Filter, Plus, Eye, Truck, Package, CheckCircle,
  Clock, AlertTriangle, MapPin, Calendar, User, Phone,
  Download, Upload, Scan, BarChart3, ArrowUpRight
} from 'lucide-react';

const ShippingManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedShipments, setSelectedShipments] = useState<string[]>([]);

  const shipments = [
    {
      id: 'SHP-001',
      orderId: 'ORD-001',
      customer: {
        name: 'John Smith',
        company: 'Smith Trading Co.',
        address: '123 Main St, New York, NY 10001, USA',
        phone: '+1-555-0123'
      },
      items: [
        { name: 'Premium Cotton T-Shirt', quantity: 50, weight: '2.5 kg' }
      ],
      totalWeight: '2.5 kg',
      totalValue: 1499.50,
      carrier: 'DHL Express',
      trackingNumber: 'DHL123456789',
      status: 'ready_to_ship',
      priority: 'high',
      shipDate: '2024-02-28',
      estimatedDelivery: '2024-03-05',
      shippingCost: 45.00,
      packageType: 'Box',
      dimensions: '40x30x20 cm',
      pickedBy: 'Worker A',
      packedBy: 'Worker B'
    },
    {
      id: 'SHP-002',
      orderId: 'ORD-002',
      customer: {
        name: 'Sarah Johnson',
        company: 'Johnson Imports',
        address: '456 Oxford St, London W1C 1AP, UK',
        phone: '+44-20-7946-0958'
      },
      items: [
        { name: 'Cotton Polo Shirt', quantity: 25, weight: '1.8 kg' }
      ],
      totalWeight: '1.8 kg',
      totalValue: 874.75,
      carrier: 'FedEx International',
      trackingNumber: 'FDX987654321',
      status: 'in_transit',
      priority: 'medium',
      shipDate: '2024-02-25',
      estimatedDelivery: '2024-03-02',
      shippingCost: 38.50,
      packageType: 'Envelope',
      dimensions: '35x25x15 cm',
      pickedBy: 'Worker C',
      packedBy: 'Worker D'
    },
    {
      id: 'SHP-003',
      orderId: 'ORD-003',
      customer: {
        name: 'Michael Chen',
        company: 'Chen Trading Ltd.',
        address: '789 Marina Bay, Singapore 018956',
        phone: '+65-6123-4567'
      },
      items: [
        { name: 'Casual Cotton Pants', quantity: 30, weight: '4.2 kg' }
      ],
      totalWeight: '4.2 kg',
      totalValue: 1199.70,
      carrier: 'UPS Worldwide',
      trackingNumber: 'UPS456789123',
      status: 'delivered',
      priority: 'medium',
      shipDate: '2024-02-20',
      estimatedDelivery: '2024-02-28',
      shippingCost: 52.00,
      packageType: 'Box',
      dimensions: '45x35x25 cm',
      pickedBy: 'Worker E',
      packedBy: 'Worker F'
    },
    {
      id: 'SHP-004',
      orderId: 'ORD-004',
      customer: {
        name: 'Emma Wilson',
        company: 'Wilson Retail',
        address: '321 George St, Sydney NSW 2000, Australia',
        phone: '+61-2-9876-5432'
      },
      items: [
        { name: 'Premium Cotton Hoodie', quantity: 15, weight: '3.6 kg' }
      ],
      totalWeight: '3.6 kg',
      totalValue: 689.85,
      carrier: 'Australia Post',
      trackingNumber: 'AP789123456',
      status: 'picking',
      priority: 'low',
      shipDate: '2024-03-01',
      estimatedDelivery: '2024-03-08',
      shippingCost: 41.25,
      packageType: 'Box',
      dimensions: '42x32x22 cm',
      pickedBy: 'Worker G',
      packedBy: null
    },
    {
      id: 'SHP-005',
      orderId: 'ORD-005',
      customer: {
        name: 'David Brown',
        company: 'Brown Enterprises',
        address: '654 Queen St, Toronto ON M5V 2B7, Canada',
        phone: '+1-416-555-0199'
      },
      items: [
        { name: 'Cotton Casual Shirt', quantity: 20, weight: '2.1 kg' }
      ],
      totalWeight: '2.1 kg',
      totalValue: 659.80,
      carrier: 'Canada Post',
      trackingNumber: null,
      status: 'pending',
      priority: 'medium',
      shipDate: null,
      estimatedDelivery: null,
      shippingCost: 28.75,
      packageType: 'Envelope',
      dimensions: '38x28x18 cm',
      pickedBy: null,
      packedBy: null
    }
  ];

  const filteredShipments = shipments.filter(shipment => {
    const matchesSearch = shipment.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         shipment.orderId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         shipment.customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (shipment.trackingNumber && shipment.trackingNumber.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || shipment.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const handleSelectShipment = (shipmentId: string) => {
    setSelectedShipments(prev => 
      prev.includes(shipmentId) 
        ? prev.filter(id => id !== shipmentId)
        : [...prev, shipmentId]
    );
  };

  const handleSelectAll = () => {
    setSelectedShipments(
      selectedShipments.length === filteredShipments.length 
        ? [] 
        : filteredShipments.map(shipment => shipment.id)
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </span>;
      case 'picking':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Package className="h-3 w-3 mr-1" />
          Picking
        </span>;
      case 'ready_to_ship':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Ready to Ship
        </span>;
      case 'in_transit':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <Truck className="h-3 w-3 mr-1" />
          In Transit
        </span>;
      case 'delivered':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Delivered
        </span>;
      default:
        return null;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          High
        </span>;
      case 'medium':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Medium
        </span>;
      case 'low':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Low
        </span>;
      default:
        return null;
    }
  };

  const totalShipments = shipments.length;
  const readyToShip = shipments.filter(s => s.status === 'ready_to_ship').length;
  const inTransit = shipments.filter(s => s.status === 'in_transit').length;
  const delivered = shipments.filter(s => s.status === 'delivered').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Shipping Management</h1>
              <p className="text-gray-600">Manage outbound shipments and deliveries</p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2">
                <Scan className="h-4 w-4" />
                <span>Scan Package</span>
              </button>
              <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2">
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>
              <button className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2">
                <Plus className="h-4 w-4" />
                <span>Create Shipment</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by shipment ID, order ID, customer, or tracking number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="picking">Picking</option>
                <option value="ready_to_ship">Ready to Ship</option>
                <option value="in_transit">In Transit</option>
                <option value="delivered">Delivered</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Shipments</p>
                <p className="text-2xl font-bold text-gray-900">{totalShipments}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Ready to Ship</p>
                <p className="text-2xl font-bold text-gray-900">{readyToShip}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Truck className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">In Transit</p>
                <p className="text-2xl font-bold text-gray-900">{inTransit}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Delivered</p>
                <p className="text-2xl font-bold text-gray-900">{delivered}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Shipments Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Shipments ({filteredShipments.length})</h2>
              {selectedShipments.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{selectedShipments.length} selected</span>
                  <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">Bulk Ship</button>
                  <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">Print Labels</button>
                </div>
              )}
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedShipments.length === filteredShipments.length && filteredShipments.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shipment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Carrier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredShipments.map((shipment) => (
                  <tr key={shipment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedShipments.includes(shipment.id)}
                        onChange={() => handleSelectShipment(shipment.id)}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{shipment.id}</div>
                        <div className="text-sm text-gray-500">Order: {shipment.orderId}</div>
                        {shipment.trackingNumber && (
                          <div className="text-xs text-blue-600">Track: {shipment.trackingNumber}</div>
                        )}
                        <div className="mt-1">{getPriorityBadge(shipment.priority)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{shipment.customer.name}</div>
                        <div className="text-sm text-gray-500">{shipment.customer.company}</div>
                        <div className="flex items-center text-xs text-gray-400 mt-1">
                          <MapPin className="h-3 w-3 mr-1" />
                          {shipment.customer.address.split(',')[0]}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        {shipment.items.map((item, index) => (
                          <div key={index} className="text-sm text-gray-900">
                            {item.name} (x{item.quantity})
                          </div>
                        ))}
                        <div className="text-xs text-gray-500 mt-1">
                          Weight: {shipment.totalWeight}
                        </div>
                        <div className="text-xs text-gray-500">
                          Value: ${shipment.totalValue.toLocaleString()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{shipment.carrier}</div>
                      <div className="text-xs text-gray-500">Cost: ${shipment.shippingCost}</div>
                      <div className="text-xs text-gray-500">{shipment.packageType}</div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(shipment.status)}
                      {shipment.pickedBy && (
                        <div className="text-xs text-gray-500 mt-1">
                          Picked: {shipment.pickedBy}
                        </div>
                      )}
                      {shipment.packedBy && (
                        <div className="text-xs text-gray-500">
                          Packed: {shipment.packedBy}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {shipment.shipDate ? `Ship: ${shipment.shipDate}` : 'Not scheduled'}
                      </div>
                      {shipment.estimatedDelivery && (
                        <div className="text-xs text-gray-500">
                          ETA: {shipment.estimatedDelivery}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button className="text-gray-400 hover:text-gray-600" title="View Details">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600" title="Track">
                          <Truck className="h-4 w-4" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600" title="Print Label">
                          <Download className="h-4 w-4" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600" title="Ship">
                          <ArrowUpRight className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingManagement;